// React hooks for state and lifecycle
import { useState, useEffect } from 'react';
// Cookie library for session and user data management
import Cookies from 'js-cookie';
// Utility functions for manipulating item names and formatting
import { getPlainName, appendDetailsToItemName } from './FP_foodPantryUtils.js';

// Backend API base URL from environment variable
const url = process.env.REACT_APP_BACKURL;

// Custom hook to manage all state and logic for the food pantry dashboard
export function useFoodPantryData() {
  // Inventory and transaction data
  const [inventory, setInventory] = useState([]); // Array of inventory items
  const [transactions, setTransactions] = useState([]); // Array of transaction records
  const [itemDetails, setItemDetails] = useState([]); // Details for selected item
  const [filteredItems, setFilteredItems] = useState([]); // Filtered items for modal
  const [itemVariations, setItemVariations] = useState([]); // Variations for selected item

  // Organization selection state
  const [organization, setOrganization] = useState([]); // List of organizations
  const [selectedOrg, setSelectedOrg] = useState(""); // Name of selected organization
  const [selectedOrgID, setSelectedOrgID] = useState(""); // ID of selected organization
  const [showOrgSelector, setShowOrgSelector] = useState(true); // Show/hide org dropdown
  const [isRootUser, setIsRootUser] = useState(false); // Whether user is root/super admin

  // Modal visibility toggles
  const [showTransactionReceiveModal, setShowTransactionReceiveModal] = useState(false);
  const [showTransactionReleaseModal, setShowTransactionReleaseModal] = useState(false);
  const [showTransactionOtherModal, setShowTransactionOtherModal] = useState(false);
  const [showItemDetailsModal, setShowItemDetailsModal] = useState(false);
  const [showFakeItemDetailsModal, setShowFakeItemDetailsModal] = useState(false);
  const [showCreationModal, setShowCreationModal] = useState(false);
  const [showEditItemModal, setShowEditItemModal] = useState(false);
  const [showVariationModal, setShowVariationModal] = useState(false);

  // Notification banners (stacked/queued)
  const [notifications, setNotifications] = useState([]);

  // On organization list change, check for OrgID cookie and set selected org
  useEffect(() => {
    let cookieOrgID = Cookies.get('OrgID');

    // If cookie is a JSON array, parse and extract orgID
    if (cookieOrgID && cookieOrgID.startsWith('j:')) {
      try {
        const orgArr = JSON.parse(cookieOrgID.slice(2));
        if (Array.isArray(orgArr) && orgArr.length > 0 && orgArr[0].orgID) {
          cookieOrgID = orgArr[0].orgID;
        } else {
          cookieOrgID = null;
        }
      } catch (err) {
        console.error("Error parsing OrgID cookie:", err);
        cookieOrgID = null;
      }
    }

    // If orgID found, set selected org and hide selector
    if (cookieOrgID) {
      setSelectedOrgID(cookieOrgID);
      setShowOrgSelector(false);

      // Set selectedOrg to org name if available in organization list
      if (organization && organization.length > 0) {
        const matchedOrg = organization.find(org => org.orgID === cookieOrgID);
        if (matchedOrg) {
          setSelectedOrg(matchedOrg.name);
        }
      }
    } else {
      setShowOrgSelector(true);
    }
  }, [organization]);

  // Fetch organization data for dropdown on mount
  useEffect(() => {
    const checkUserRoleAndFetchOrganizations = async () => {
      try {
        const sessionID = Cookies.get('sessionID');
        if (!sessionID) {
          console.error("Session ID not found");
          return;
        }

        // Check user role
        const roleResponse = await fetch(process.env.REACT_APP_BACKURL + '/api/foodPantry/user/role', {
          headers: {
            'Authorization': `Bearer ${sessionID}`,
          },
          credentials: 'include',
        });

        if (!roleResponse.ok) {
          throw new Error(`Role check error! Status: ${roleResponse.status}`);
        }

        const roleData = await roleResponse.json();
        const { roleID } = roleData;

        if (roleID === "0") {
          // Root user - fetch all organizations
          setIsRootUser(true);
          const orgResponse = await fetch(process.env.REACT_APP_BACKURL + '/api/foodPantry/organization', {
            credentials: 'include',
          });
          if (!orgResponse.ok) throw new Error(`Organization error! Status: ${orgResponse.status}`);

          const orgData = await orgResponse.json();
          if (orgData?.OrgNames?.length) {
            setOrganization(orgData.OrgNames);
            setSelectedOrg(orgData.OrgNames[0].name);
            setSelectedOrgID(orgData.OrgNames[0].orgID);
          }
        } else {
          // Non-root user - fetch only their organization
          setIsRootUser(false);
          let cookieOrgID = Cookies.get('OrgID');
          
          // Parse JSON-encoded cookie if necessary
          if (cookieOrgID && cookieOrgID.startsWith('j:')) {
            try {
              const parsed = JSON.parse(cookieOrgID.slice(2));
              // Handle both array and object formats
              if (Array.isArray(parsed) && parsed.length > 0 && parsed[0].orgID) {
                cookieOrgID = parsed[0].orgID;
              } else if (typeof parsed === 'string' && parsed !== 'null') {
                cookieOrgID = parsed;
              } else {
                cookieOrgID = null;
              }
            } catch (err) {
              console.error("Error parsing OrgID cookie:", err);
              cookieOrgID = null;
            }
          }
          
          // Only proceed if we have a valid orgID
          if (cookieOrgID && cookieOrgID !== 'null') {
            try {
              const orgResponse = await fetch(process.env.REACT_APP_BACKURL + `/api/foodPantry/organization/${cookieOrgID}`, {
                credentials: 'include',
              });
              if (!orgResponse.ok) throw new Error(`Organization error! Status: ${orgResponse.status}`);

              const orgData = await orgResponse.json();
              if (orgData?.organization) {
                const orgObj = {
                  orgID: cookieOrgID,
                  name: orgData.organization.organizationName
                };
                setOrganization([orgObj]);
                setSelectedOrg(orgData.organization.organizationName);
                setSelectedOrgID(cookieOrgID);
              }
            } catch (err) {
              console.error('Error fetching organization data:', err);
            }
          } else {
            console.error('No valid OrgID found in cookie');
          }
        }
      } catch (error) {
        console.error('Error checking user role or fetching organizations:', error);
      }
    };
    checkUserRoleAndFetchOrganizations();
  }, []);

  // Fetch volunteer's active organization from backend when not root user
  useEffect(() => {
    const fetchVolunteerActiveOrg = async () => {
      if (isRootUser) return; // Root users don't have activeOrgID
      
      try {
        const response = await fetch(`${url}/api/foodPantry/volunteer/active-org`, {
          credentials: 'include',
        });

        if (!response.ok) {
          console.error("Failed to fetch volunteer active organization");
          return;
        }

        const data = await response.json();
        const { activeOrgID, isActive } = data;

        if (activeOrgID && isActive) {
          // Volunteer is clocked in - fetch their organization data
          const orgResponse = await fetch(`${url}/api/foodPantry/organization/${activeOrgID}`, {
            credentials: 'include',
          });

          if (orgResponse.ok) {
            const orgData = await orgResponse.json();
            if (orgData?.organization) {
              const orgObj = {
                orgID: activeOrgID,
                name: orgData.organization.organizationName
              };
              setOrganization([orgObj]);
              setSelectedOrgID(activeOrgID);
              setSelectedOrg(orgData.organization.organizationName);
              setShowOrgSelector(false);
            }
          }
        } else {
          // Volunteer is not clocked in - clear organization
          setOrganization([]);
          setSelectedOrgID('');
          setSelectedOrg('');
          setShowOrgSelector(true);
        }
      } catch (error) {
        console.error('Error fetching volunteer active organization:', error);
      }
    };

    fetchVolunteerActiveOrg();
  }, [isRootUser]);

  // Fetch inventory data when selected organization changes
  useEffect(() => {
    const fetchInventory = async () => {
      try {
        if (!url) throw new Error("REACT_APP_BACKURL is not defined");
        if (!organization) throw new Error("Organization is not defined");
        if (!selectedOrgID) throw new Error("Selected organization ID is not defined");

        const response = await fetch(`${url}/api/foodPantry/inventory/${selectedOrgID}?timezoneOffset=${-(new Date().getTimezoneOffset())}`, {
          credentials: "include"
        });
        if (!response.ok) throw new Error("Failed to fetch inventory");
        const data = await response.json();
        // Remove orgID from each item and append details for display
        const updatedData = data.map((item, index) => {
          const { orgID, ...cleanedItem } = item;
          appendDetailsToItemName(cleanedItem, index);
          return cleanedItem;
        });
        setInventory(updatedData);
      } catch (error) {
        console.error('Error fetching inventory:', error);
      }
    };

    if (selectedOrg) {
      fetchInventory();
    }
  }, [selectedOrg]);

  // Refresh inventory when any modal closes (ensures up-to-date data)
  useEffect(() => {
    const fetchInventory = async () => {
      try {
        if (!url) throw new Error("REACT_APP_BACKURL is not defined");
        if (!organization) throw new Error("Organization is not defined");
        if (!selectedOrgID) throw new Error("Selected organization ID is not defined");

        const response = await fetch(`${url}/api/foodPantry/inventory/${selectedOrgID}?timezoneOffset=${-(new Date().getTimezoneOffset())}`, {
          credentials: "include"
        });
        if (!response.ok) throw new Error("Failed to fetch inventory");
        const data = await response.json();
        const updatedData = data.map((item, index) => {
          const { orgID, ...cleanedItem } = item;
          appendDetailsToItemName(cleanedItem, index);
          return cleanedItem;
        });
        setInventory(updatedData);
      } catch (error) {
        console.error('Error fetching inventory:', error);
      }
    };

    // Only fetch if all modals are closed and org is selected
    if (
      !showTransactionReceiveModal &&
      !showTransactionReleaseModal &&
      !showTransactionOtherModal &&
      !showItemDetailsModal &&
      !showCreationModal &&
      !showEditItemModal &&
      !showVariationModal &&
      selectedOrgID
    ) {
      fetchInventory();
    }
  }, [
    showTransactionReceiveModal,
    showTransactionReleaseModal,
    showTransactionOtherModal,
    showItemDetailsModal,
    showCreationModal,
    showEditItemModal,
    showVariationModal,
    selectedOrgID
  ]);

  // Attach click handlers to item links after inventory loads
  useEffect(() => {
    const tableContainer = document.getElementById('inventory-table-container');
    if (!tableContainer) {
      console.error('Inventory table container not found.');
      return;
    }

    const handleLinkClick = (e) => {
      // Check if the clicked element is an anchor tag and its ID starts with 'linkItemDetails'
      if (e.target.tagName === 'A' && e.target.id.startsWith('linkItemDetails')) {
        e.preventDefault();
        const selectedID = e.target.id;
        const selectedIndex = parseInt(selectedID.replace('linkItemDetails', ''), 10);
        const selectedItem = inventory[selectedIndex];
        if (!selectedItem) {
          return;
        }

        // Get plain name and filter all items with the same name
        const plainSelectedName = getPlainName(selectedItem.itemName);
        const filtered = inventory.filter(item => getPlainName(item.itemName) === plainSelectedName);

        setItemDetails([selectedItem]);
        setFilteredItems(filtered);
        setShowItemDetailsModal(true);
      }
    };

    // Add a single click event listener to the parent container
    tableContainer.addEventListener('click', handleLinkClick);

    // Clean up the event listener when the component unmounts
    return () => {
      tableContainer.removeEventListener('click', handleLinkClick);
    };

  }, [inventory]);

  // Fetch transaction data for selected organization
  useEffect(() => {
    const fetchTransactions = async () => {
      try {
        const response = await fetch(`${url}/api/foodPantry/inventory/${selectedOrgID}/transactions?timezoneOffset=${-(new Date().getTimezoneOffset())}`, {
          credentials: "include"
        });
        if (!response.ok) throw new Error("Failed to fetch transactions");
        const data = await response.json();
        // Format transaction dates and values for display
        const formatted = data.map(t => ({
          ...t,
          transactionDate: new Date(t.transactionDate).toLocaleDateString(),
          expirationDate: new Date(t.expirationDate).toLocaleDateString(),
          unitPrice: `$${parseFloat(t.unitPrice).toFixed(2)}`,
          transactionValue: `$${parseFloat(t.transactionValue).toFixed(2)}`
        }));
        setTransactions(formatted);
      } catch (error) {
        console.error("Transactions fetch error:", error);
      }
    };

    // Only fetch if all modals are closed and org is selected
    if (
      !showTransactionReceiveModal &&
      !showTransactionReleaseModal &&
      !showTransactionOtherModal &&
      !showItemDetailsModal &&
      selectedOrgID
    ) {
      fetchTransactions();
    }
  }, [
    showTransactionReceiveModal,
    showTransactionReleaseModal,
    showTransactionOtherModal,
    showItemDetailsModal,
    selectedOrgID,
    url
  ]);

  // Helper to set selected organization ID based on org name
  const handleSelectedOrgID = (orgName) => {
    const matchedOrg = organization.find(org => org.name === orgName);
    if (matchedOrg) {
      setSelectedOrgID(matchedOrg.orgID);
    } else {
      setSelectedOrgID(null);
      console.error("Organization ID not found for the selected organization.");
    }
  };

  // Handler for organization dropdown change
  const handleOrgChange = (e) => {
    // Only root users can change organizations
    if (isRootUser) {
      const selectedName = e.target.value;
      setSelectedOrg(selectedName);
      handleSelectedOrgID(selectedName);
    }
  };

  // Return all state and handlers for use in dashboard components
  return {
    inventory,
    setInventory,
    transactions,
    organization,
    selectedOrg,
    selectedOrgID,
    showOrgSelector,
    isRootUser,
    showTransactionReceiveModal,
    setShowTransactionReceiveModal,
    showFakeItemDetailsModal,
    setShowFakeItemDetailsModal,
    showTransactionReleaseModal,
    setShowTransactionReleaseModal,
    showTransactionOtherModal,
    setShowTransactionOtherModal,
    showItemDetailsModal,
    setShowItemDetailsModal,
    showCreationModal,
    setShowCreationModal,
    showEditItemModal,
    setShowEditItemModal,
    showVariationModal,
    setShowVariationModal,
    handleOrgChange,
    notifications,
    setNotifications,
    itemDetails,
    setItemDetails,
    filteredItems,
    setFilteredItems
  };
}